'use client'

import { ClickAwayListener, Grow, Paper, Popper } from '@mui/material'
import { type SyntheticEvent, useCallback, useRef, useState } from 'react'

import {
  type GetCategoriesQueryVariables,
  type Category,
  type Maybe,
} from '@/lib/generated/graphql'
import { Button } from '@/components/library/button'
import { CategoryLevel } from './category-dynamic'

interface ICategoryFilterProps {
  title: string
  selected: string[]
  onFilterSelect?: (id: string) => void
  categoryLevel?: Maybe<number>
  categories: Category[]
  selectedCats: Category[]
  getCategoriesQueryVariables: GetCategoriesQueryVariables
  children?: React.ReactNode
}

export const CategoryFilter = ({
  children,
  categories,
  selectedCats,
  getCategoriesQueryVariables,
  selected,
  ...props
}: ICategoryFilterProps) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event | SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  const setPopperHeight = useCallback(() => {
    if (typeof window === 'undefined') {
      return '0px'
    }
    const body = typeof window === 'undefined' ? 0 : document.body.scrollHeight
    const offset = anchorRef?.current
      ? window.pageYOffset + anchorRef?.current.getBoundingClientRect().top
      : 0
    return `${body - offset - 50}px`
  }, [anchorRef])

  return (
    <div>
      <Button
        ref={anchorRef}
        id='category-filter-button'
        aria-controls={open ? 'category-filter-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        data-testid='filter-title'
        className='ml-0 min-w-16 text-primary-dark no-underline'
      >
        {props?.title}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement='bottom-start'
        style={{
          position: 'relative',
          zIndex: 999,
          maxHeight: setPopperHeight(),
          overflow: 'auto',
          minWidth: '350px',
        }}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <div className='overflow-y-auto overflow-x-hidden'>
                  <CategoryLevel
                    selected={selectedCats}
                    open
                    categories={categories}
                    productFilters={{
                      ...getCategoriesQueryVariables.productFilters,
                      filterbyCategories: selected,
                    }}
                  />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}
